import styled from '@emotion/styled';
import { Popover, TextInput, Text, PopoverDropdownProps } from '@mantine/core';
import { FillerSwitch } from '@root/Design/Filler';
import { useReadonlyInputStyles } from '@root/Design/Primitives';
import { EventEmitter, useEvent, useToggle } from '@root/Services/EventEmitter';
import { useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { useRouteBoundPortal } from '../Router/RouteBoundPortal';
import { VirtualTree } from '../VirtualTree';
import { VirtualTreeModel } from '../VirtualTree/VirtualTreeModel';
import { DropdownOpener } from './Design';
import { FlyoverOverlay } from './Flyover';
import { Picker, PickerProps } from './Picker';

type ItemType = { value: string; label?: ReactNode };
type InternalItemType = { value: string; lcValue: string; label?: ReactNode };
export type ComboPickerProps = Partial<Omit<PickerProps<ItemType>, 'items' | 'onChange'>>;
export type ComboPopoverProps = Partial<React.ComponentPropsWithoutRef<typeof Popover>>;
export type ComboTextInputProps = React.ComponentPropsWithoutRef<typeof TextInput>;
export function Combobox({
    value,
    onChange,
    options,
    lazyLoad,
    disabled,
    inputProps,
    popoverProps,
    popoverContainerProps: popoverContainerPropsOverride,
    pickerProps,
    pickerHeader,
}: {
    value: string;
    onChange: (value: string) => void;
    options: (filter: string) => Promise<ItemType[]>;
    lazyLoad?: boolean;
    disabled?: boolean;
    inputProps?: ComboTextInputProps;
    popoverProps?: ComboPopoverProps;
    popoverContainerProps?: PopoverDropdownProps;
    pickerProps?: ComboPickerProps;
    pickerHeader?: ReactNode;
}) {
    const [opened, { close, toggle, open }] = useToggle(false);
    const [filterText, setFilterText] = useState('');
    const [tree, setTree] = useState<VirtualTree>();
    const [treeModel, setTreeModel] = useState<VirtualTreeModel<InternalItemType>>();
    const input = useRef<HTMLInputElement>(null);
    const dropdown = useRef<HTMLDivElement>(null);

    const dataLoader = useMemo(() => {
        const invalidated = EventEmitter.empty();
        let loaded = false;
        const result = {
            loading: !lazyLoad,
            availableValues: [] as ItemType[],
            invalidated,
            loadValues: async () => {
                result.loading = true;
                try {
                    result.availableValues = await options(filterText);
                    loaded = true;
                } finally {
                    result.loading = false;
                    invalidated.emit();
                }
            },
            requireValues: () => {
                if (!result.loading && !loaded) {
                    result.loadValues();
                }
            },
            init: () => {
                if (!lazyLoad) {
                    result.loadValues();
                }
            },
        };

        return result;
    }, [options, lazyLoad]);

    useEvent(dataLoader.invalidated);
    useEffect(() => dataLoader.init(), [dataLoader]);

    const onDropdownClick = useCallback((evt: MouseEvent) => {
        evt.preventDefault();
        if (input.current) {
            input.current.focus();
        }
    }, []);

    const handleValuePicklistSelect = useCallback(
        (items: unknown[]) => {
            const typedItems = items as { value: string }[];
            if (typedItems.length) {
                onChange(typedItems[0].value);
                close();
                if (dropdown.current) {
                    dropdown.current.removeEventListener('click', onDropdownClick);
                }
            }
        },
        [close, onChange]
    );

    const openPicker = useCallback(() => {
        if (dropdown.current) {
            dropdown.current.addEventListener('click', onDropdownClick);
        }
        setFilterText('');
        open();
        dataLoader.requireValues();
    }, [open, setFilterText, dropdown, dataLoader]);

    const handleValueInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            onChange(value);
            setFilterText(value);
        },
        [close, onChange]
    );

    const finalPickerProps = useMemo(
        () =>
            ({
                nameAccessor: ({ value }: { value: string }) => value,
                selections: [],
                renderItem: (item: (typeof filteredValues)[0]): ReactNode => {
                    return item.label ?? item.value;
                },
                noFilter: true,
                minimizeHeight: true,
                height: 300,
                mode: 'single' as const,
                ...pickerProps,
                onModelLoaded: (model, tree) => {
                    setTree(tree);
                    setTreeModel(model);
                },
            } as Omit<PickerProps<ItemType & { lcValue: string }>, 'items' | 'onChange'>),
        [pickerProps]
    );

    const values = useMemo(
        () => dataLoader.availableValues.map((x) => ({ ...x, lcValue: x.value.toLocaleLowerCase() })),
        [dataLoader.availableValues]
    );
    const filteredValues = useMemo(() => {
        const lcTagValue = filterText.toLowerCase();
        return values.filter((value) => value.lcValue.includes(lcTagValue));
    }, [filterText, values]);

    const {
        classes: { readonly },
    } = useReadonlyInputStyles();

    const target = useRouteBoundPortal();

    const finalPopoverProps = useMemo(
        () =>
            ({
                position: 'bottom',
                offset: 0,
                shadow: 'md',
                withinPortal: true,
                zIndex: 1000,
                target,
                ...popoverProps,
                onClose: close,
            } as React.ComponentPropsWithoutRef<typeof Popover>),
        [close, popoverProps]
    );

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
        (evt) => {
            if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
                treeModel?.navigate(evt.key.substring(5));
                const highlightedItem = treeModel?.getHighlightedItem();
                if (highlightedItem) {
                    tree?.scrollToItem(highlightedItem);
                    onChange(highlightedItem.value);
                }
            } else if (evt.key === 'Enter') {
                close();
                evt.preventDefault();
                evt.stopPropagation();
            } else if (evt.key === 'Tab') {
                close();
            } else if (evt.key === 'Escape') {
                close();
            }
        },
        [treeModel, tree]
    );

    const popoverContainerProps: PopoverDropdownProps = {
        p: 0,
        ...popoverContainerPropsOverride,
        sx: { '.vtree-container': { overflowY: 'scroll' }, borderColor: '#0003', ...popoverContainerPropsOverride?.sx },
    };

    return (
        <>
            <FlyoverOverlay onClick={close} opened={opened} />
            <Popover {...finalPopoverProps} opened={opened}>
                <Popover.Dropdown {...popoverContainerProps}>
                    <div ref={dropdown} style={{ display: 'contents' }}>
                        {pickerHeader}
                        <PickerPlaceholder loading={dataLoader.loading}>
                            <FillerSwitch loading={dataLoader.loading}>
                                {() => (
                                    <>
                                        {!opened ? null : (
                                            <Picker {...finalPickerProps} items={filteredValues} onChange={handleValuePicklistSelect} />
                                        )}
                                        {filteredValues.length ? null : (
                                            <Text size="sm" color="dimmed" py="lg" align="center" italic>
                                                Empty
                                            </Text>
                                        )}
                                    </>
                                )}
                            </FillerSwitch>
                        </PickerPlaceholder>
                    </div>
                </Popover.Dropdown>
                <Popover.Target>
                    <TextInput
                        autoComplete="off"
                        rightSection={<DropdownOpener state={{ opened, disabled, loading: dataLoader.loading }} onClick={openPicker} />}
                        disabled={disabled}
                        className={disabled ? readonly : undefined}
                        {...inputProps}
                        ref={input}
                        onKeyDown={onKeyDown}
                        onChange={handleValueInputChange}
                        value={value}
                        onFocus={openPicker}
                    />
                </Popover.Target>
            </Popover>
        </>
    );
}

const PickerPlaceholder = styled.div<{ loading?: boolean }>`
    height: ${(p) => (p.loading ? '150px' : 'auto')};
    width: ${(p) => (p.loading ? '250px' : 'auto')};
`;
